import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import {
  Lightning, Target, Users, ChartLineUp, ShieldCheck, Clock
} from "@phosphor-icons/react";

const features = [
  { icon: Lightning, title: "AI-Powered Generation", desc: "Upload a job description and get a complete technical assessment in seconds." },
  { icon: Target, title: "Skill-Targeted MCQs", desc: "Questions mapped to specific skills with difficulty levels and explanations." },
  { icon: Users, title: "Candidate Management", desc: "Invite candidates via email, track progress, and manage assessment links." },
  { icon: ChartLineUp, title: "Analytics Dashboard", desc: "Score distributions, skill breakdowns, and AI-powered hiring recommendations." },
  { icon: ShieldCheck, title: "Anti-Cheating", desc: "Tab switch detection, fullscreen warnings, and timed assessments." },
  { icon: Clock, title: "Auto-Save & Timer", desc: "Candidates never lose progress. Auto-submit when time expires." },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      const redirectUri = window.location.origin + "/dashboard";
      const nonce = Math.random().toString(36).substring(2, 15);
      const googleAuthUrl =
        `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=id_token` +
        `&scope=openid%20profile%20email` +
        `&nonce=${nonce}`;
      window.location.href = googleAuthUrl;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-20 md:pt-32 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-black/10 mb-6">
                <div className="w-1.5 h-1.5 bg-[#FF3366] rounded-full" />
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B]">
                  AI-Powered Hiring
                </span>
              </div>
              <h1 className="font-heading font-black text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-none text-[#0A0A0A] mb-3">
                Quick
                <span className="text-[#FF3366]"> Assess</span>
              </h1>
              <p className="font-heading font-semibold text-lg sm:text-xl text-[#52525B] mb-6">
                AI-Powered Technical Assessments
              </p>
              <p className="text-base leading-relaxed text-[#52525B] max-w-lg mb-8">
                Upload a job description. Our AI analyzes skills, technologies, and seniority level
                to create targeted MCQ assessments. Invite candidates, track results, get hiring recommendations.
              </p>
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleGetStarted}
                  className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-8 py-3 h-auto font-semibold text-sm transition-colors"
                  data-testid="get-started-btn"
                >
                  Get Started Free
                </Button>
                <a
                  href="#features"
                  className="text-sm font-medium text-[#0A0A0A] border-b border-[#0A0A0A] pb-0.5 hover:text-[#FF3366] hover:border-[#FF3366] transition-colors"
                  data-testid="learn-more-link"
                >
                  Learn more
                </a>
              </div>
              <p className="text-[11px] text-[#52525B] mt-4 max-w-sm">
                By signing in, you agree to our{" "}
                <Link to="/terms" className="text-[#0A0A0A] underline underline-offset-2 hover:text-[#FF3366] transition-colors">Terms</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-[#0A0A0A] underline underline-offset-2 hover:text-[#FF3366] transition-colors">Privacy Policy</Link>.
              </p>
            </div>
            <div className="md:col-span-5 animate-fade-up stagger-2">
              <div className="relative">
                <div className="border border-black/10 bg-[#F8F9FA] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-[#10B981] rounded-full" />
                    <span className="text-xs text-[#52525B] font-medium">AI Processing</span>
                  </div>
                  <div className="space-y-3">
                    <div className="h-2 bg-[#0A0A0A] w-3/4" />
                    <div className="h-2 bg-black/10 w-full" />
                    <div className="h-2 bg-black/10 w-5/6" />
                  </div>
                  <div className="mt-4 pt-4 border-t border-black/10 flex gap-2">
                    {["Python", "React", "AWS", "SQL"].map(t => (
                      <span key={t} className="text-[10px] px-2 py-0.5 bg-white border border-black/10 font-medium text-[#0A0A0A]">{t}</span>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-white border border-black/5">
                        <div className="w-5 h-5 border border-black/20 flex items-center justify-center text-[10px] font-bold">{i}</div>
                        <div className="flex-1">
                          <div className="h-1.5 bg-black/10 w-full mb-1" />
                          <div className="h-1.5 bg-black/5 w-2/3" />
                        </div>
                        <span className={`text-[9px] px-1.5 py-0.5 font-bold ${i === 1 ? 'bg-[#10B981]/10 text-[#10B981]' : i === 2 ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
                          {i === 1 ? 'EASY' : i === 2 ? 'MEDIUM' : 'HARD'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 w-full h-full border border-[#FF3366]/20 -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-black/10 py-20 md:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3366]">Features</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0A0A] mt-2">
              Everything you need to hire smarter
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10">
            {features.map((f, i) => (
              <div
                key={i}
                className="p-8 border-b md:border-b-0 md:border-r border-black/10 last:border-r-0 hover:bg-white transition-colors group"
              >
                <f.icon size={28} weight="duotone" className="text-[#FF3366] mb-4" />
                <h3 className="font-heading font-semibold text-lg text-[#0A0A0A] mb-2">{f.title}</h3>
                <p className="text-sm text-[#52525B] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0A0A] mb-4">
            Ready to streamline your hiring?
          </h2>
          <p className="text-base text-[#52525B] mb-8 max-w-md mx-auto">
            Create your first AI-powered assessment in under a minute.
          </p>
          <Button
            onClick={handleGetStarted}
            className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-8 py-3 h-auto font-semibold"
            data-testid="cta-get-started-btn"
          >
            Start Creating Assessments
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo_vector.svg" alt="Quick Assess" className="w-8 h-8" />
            <div className="flex flex-col leading-tight">
              <span className="text-base text-[#52525B]">Quick <span className="text-[#FF3366]">Assess</span></span>
              <span className="text-xs text-[#52525B]">by KnowVex</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="text-xs text-[#52525B] hover:text-[#0A0A0A] transition-colors">Terms</Link>
            <div className="w-px h-3 bg-black/10" />
            <Link to="/privacy" className="text-xs text-[#52525B] hover:text-[#0A0A0A] transition-colors">Privacy</Link>
          </div>
          <p className="text-xs text-[#52525B]">AI-powered technical assessments</p>
        </div>
      </footer>
    </div>
  );
}
